import { TileLayerProps } from 'react-leaflet';

/**
 * Centralized map tile configuration for consistent styling across all maps
 */

export interface MapTileConfig {
    url: string;
    attribution: string;
    subdomains?: string;
    maxZoom?: number;
    tileSize?: number;
    zoomOffset?: number;
}

/**
 * Tile style configurations
 */
const TILE_STYLES = {
    voyager: {
        name: 'Voyager',
        description: 'Neutral style that works well in both modes (free, no API key)',
        dark: {
            url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: 'abcd'
        },
        light: {
            url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: 'abcd'
        }
    },
    darkMatter: {
        name: 'Dark Matter',
        description: 'Very dark theme with minimal colors (hard to see details)',
        dark: {
            url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: 'abcd'
        },
        light: {
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            subdomains: 'abc'
        }
    },
    tonerLite: {
        name: 'Toner Lite',
        description: 'Light gray/white style good for dark mode (free)',
        dark: {
            url: 'https://stamen-tiles-{s}.a.ssl.fastly.net/toner-lite/{z}/{x}/{y}{r}.png',
            attribution: 'Map tiles by <a href="http://stamen.com">Stamen Design</a>, <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a> &mdash; Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            subdomains: 'abcd'
        },
        light: {
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            subdomains: 'abc'
        }
    },
    positron: {
        name: 'Positron',
        description: 'Light, minimal Carto style - very clean and bright (free, no API key)',
        dark: {
            url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: 'abcd'
        },
        light: {
            url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
            subdomains: 'abcd'
        }
    },
    openstreetmap: {
        name: 'OpenStreetMap',
        description: 'Standard OpenStreetMap style (default)',
        dark: {
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            subdomains: 'abc'
        },
        light: {
            url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            subdomains: 'abc'
        }
    }
} as const;

/**
 * Get available tile style names
 * @param isDarkMode - Whether dark mode is active. Filters out tonerLite in dark mode and darkMatter in light mode
 */
export const getAvailableTileStyles = (isDarkMode: boolean = false): Array<{ value: keyof typeof TILE_STYLES; label: string; description: string }> => {
    return Object.entries(TILE_STYLES)
        .filter(([key]) => {
            // Hide tonerLite in dark mode as it fails to load
            if (isDarkMode && key === 'tonerLite') {
                return false;
            }
            // Hide darkMatter in light mode (very dark theme doesn't work well in light mode)
            if (!isDarkMode && key === 'darkMatter') {
                return false;
            }
            return true;
        })
        .map(([key, style]) => ({
            value: key as keyof typeof TILE_STYLES,
            label: style.name,
            description: style.description
        }));
};

/**
 * Get tile layer configuration based on theme mode and style
 * @param isDarkMode - Whether dark mode is active
 * @param style - Tile style to use (default: 'voyager')
 * @returns Tile layer props for react-leaflet
 */
const getTileConfig = (
    isDarkMode: boolean,
    style: keyof typeof TILE_STYLES = 'voyager'
): Omit<TileLayerProps, 'url' | 'attribution'> & { url: string; attribution: string } => {
    const styleConfig = TILE_STYLES[style];
    const mode = isDarkMode ? 'dark' : 'light';
    return styleConfig[mode];
};

/**
 * Get tile layer configuration using Voyager style (default)
 * Voyager is a neutral style that works well in both modes (free, no API key required)
 */
export const getMapTileConfig = (isDarkMode: boolean) => {
    return getTileConfig(isDarkMode, 'voyager');
};

/**
 * Get tile layer configuration using Carto Voyager style
 * Voyager is a neutral style that works well in both modes (free, no API key required)
 */
export const getMapTileConfigVoyager = (isDarkMode: boolean) => {
    return getTileConfig(isDarkMode, 'voyager');
};

/**
 * Primary function to get map tile configuration
 * Uses Voyager style as default (neutral style that works well in both modes)
 */
export const getMapTiles = (isDarkMode: boolean, style: keyof typeof TILE_STYLES = 'voyager') => {
    return getTileConfig(isDarkMode, style);
};

export type MapTileStyleKey = keyof typeof TILE_STYLES;

/** Read saved tile style from localStorage (for entity detail map tabs). */
export function getSavedTileStyle(): MapTileStyleKey {
    const saved = localStorage.getItem('mapTileStyle');
    if (saved && Object.keys(TILE_STYLES).includes(saved)) {
        return saved as MapTileStyleKey;
    }
    return 'voyager';
}

